import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { seoFixQueue, seoFixAction } from "@/lib/seo/seo-os.functions";
import { SeoPage, Stat, Pill, Loading, Empty, num } from "@/components/admin/seo/SeoUi";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/seo/fix-center")({
  head: () => ({ meta: [{ title: "Fix Center · Admin" }, { name: "robots", content: "noindex, nofollow" }] }),
  component: FixCenterPage,
});

const TONE: Record<string, string> = { critical: "bad", warning: "warn", info: "neutral" };

function FixCenterPage() {
  const load = useServerFn(seoFixQueue);
  const act = useServerFn(seoFixAction);
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["seo-fix-queue"], queryFn: () => load() });
  const d = q.data as any;
  const items = (d?.items ?? []) as any[];

  const autoRun = useMutation({
    mutationFn: () => act({ data: { action: "auto_run" } }),
    onSuccess: (r: any) => {
      toast.success(`Scanned ${r.scanned} issues — ${r.applied} applied, ${r.awaitingApproval} awaiting your approval.`);
      qc.invalidateQueries({ queryKey: ["seo-fix-queue"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Could not run the fixes."),
  });

  const counts = items.reduce<Record<string, number>>((acc, i) => {
    acc[i.severity] = (acc[i.severity] ?? 0) + 1;
    return acc;
  }, {});

  return (
    <SeoPage
      title="Fix Center"
      description="Every issue the system found, with the evidence behind it. Fixes it can propose are checked against your advertising and mortgage rules first; anything sensitive waits for your approval."
      actions={
        <Button onClick={() => autoRun.mutate()} disabled={autoRun.isPending}>
          {autoRun.isPending ? "Running…" : "Run safe fixes"}
        </Button>
      }
    >
      {q.isLoading ? (
        <Loading />
      ) : !d?.sources?.crawl ? (
        <Empty>No site scan has run yet. Start one on the Site health screen, then come back.</Empty>
      ) : items.length === 0 ? (
        <Empty>No open issues found in the latest scan.</Empty>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-4">
            <Stat label="Open issues" value={num(items.length)} />
            <Stat label="Critical" value={num(counts.critical ?? 0)} />
            <Stat label="Warnings" value={num(counts.warning ?? 0)} />
            <Stat label="Can be fixed automatically" value={num(items.filter((i) => i.autoFixable).length)} />
          </div>
          <div className="mt-6 grid gap-3">
            {items.slice(0, 100).map((item) => (
              <FixRow key={item.id} item={item} />
            ))}
          </div>
        </>
      )}
    </SeoPage>
  );
}

function FixRow({ item }: { item: any }) {
  const act = useServerFn(seoFixAction);
  const [preview, setPreview] = useState<any>(null);

  const doPreview = useMutation({
    mutationFn: () => act({ data: { action: "preview", item } }),
    onSuccess: (r) => setPreview(r),
    onError: (e: any) => toast.error(e?.message ?? "Preview failed."),
  });
  const doApply = useMutation({
    mutationFn: () => act({ data: { action: "apply", item } }),
    onSuccess: (r: any) => toast.success(r?.reason ? `${r.status}: ${r.reason}` : "Proposed."),
    onError: (e: any) => toast.error(e?.message ?? "Could not propose this fix."),
  });

  return (
    <div className="rounded-xl border border-border bg-background p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Pill tone={TONE[item.severity]}>{item.severity}</Pill>
            <span className="font-semibold">{item.title}</span>
          </div>
          <p className="mt-1 break-words text-sm text-muted-foreground">{item.path}</p>
          <p className="mt-1 text-sm">{item.detail}</p>
        </div>
        {item.proposal ? (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => doPreview.mutate()} disabled={doPreview.isPending}>
              Preview
            </Button>
            <Button size="sm" onClick={() => doApply.mutate()} disabled={doApply.isPending}>
              Apply
            </Button>
          </div>
        ) : null}
      </div>
      {preview ? (
        <div className="mt-3 rounded-lg bg-muted p-3 text-sm">
          {preview.applicable ? (
            <>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Current</p>
              <p className="mb-2 break-words">{preview.before ?? "— none —"}</p>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Proposed</p>
              <p className="break-words">{preview.after}</p>
              {preview.locked ? <p className="mt-2 text-amber-700">This page is protected and cannot be changed automatically.</p> : null}
              {preview.qa?.issues?.length ? (
                <p className="mt-2 text-red-700">Blocked: {preview.qa.issues.join(" ")}</p>
              ) : null}
            </>
          ) : (
            <p>{preview.reason}</p>
          )}
        </div>
      ) : null}
    </div>
  );
}
